import { useState, useEffect } from "react";
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface PaymentFormProps {
  therapistName: string;
  sessionDate: Date;
  amount: number;
}

export default function PaymentForm({ therapistName, sessionDate, amount }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setPaymentError(null);

    try {
      // Primeiro, tentamos criar a sessão manualmente
      const pathParts = window.location.pathname.split("/");
      const therapistId = parseInt(pathParts[2]); // Captura o ID do terapeuta da URL /checkout/ID/...
      
      console.log("Criando sessão para terapeuta ID:", therapistId);
      
      await apiRequest("POST", "/api/sessions", {
        therapist_id: therapistId,
        date: sessionDate.toISOString(),
        is_free: false,
        status: "scheduled",
      });
      
      // Após criar a sessão, confirmamos o pagamento
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/sessions`,
        },
      });

      if (error) {
        setPaymentError(error.message || "Um erro ocorreu durante o pagamento");
        toast({
          title: "Falha no Pagamento",
          description: error.message || "Um erro ocorreu durante o pagamento",
          variant: "destructive",
        });
        setIsLoading(false);
      } else {
        // Payment succeeded and redirect will happen automatically
        toast({
          title: "Pagamento bem-sucedido",
          description: "Obrigado pelo seu pagamento!",
        });
        
        // Invalidar consultas para atualizar dados
        queryClient.invalidateQueries({ queryKey: ["/api/sessions"] });
      }
    } catch (error: any) {
      setPaymentError(error.message || "Um erro ocorreu durante o agendamento");
      toast({
        title: "Falha no Agendamento",
        description: error.message || "Um erro ocorreu durante o agendamento",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-primary/5 rounded-lg p-4 mb-4">
        <h3 className="font-semibold text-lg mb-2">Resumo da sessão</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Terapeuta:</span>
            <span className="font-medium">{therapistName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Data:</span>
            <span className="font-medium">{formatDate(sessionDate)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Horário:</span>
            <span className="font-medium">{formatTime(sessionDate)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Valor:</span>
            <span className="font-semibold text-primary">{formatCurrency(amount)}</span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-semibold text-lg">Dados de pagamento</h3>
        <PaymentElement />
        
        {paymentError && (
          <div className="text-red-500 text-sm mt-2">
            {paymentError}
          </div>
        )}
        
        <div className="pt-4">
          <Button 
            type="submit" 
            disabled={!stripe || !elements || isLoading} 
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processando...
              </>
            ) : (
              `Pagar ${formatCurrency(amount)}`
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
